import type { DocumentDto } from '@dbrain/shared';
import { Button, Icons } from '@dbrain/ui';
import { useTranslation } from 'react-i18next';
import type { Attachment, ChatItem } from '@dbrain/app-core/conversation-state';
import { formatBytes } from '@dbrain/app-core/format';
import { currentLocale } from '../../i18n/index.js';
import { DocumentState, documentSize, KindBadge } from '../documents/DocumentParts.js';
import { changeStats, DiffView } from './SessionParts.js';

/**
 * Parts of a conversation that come from files and tools (SA-068, D-35).
 * In chat spaces a tool call is a plain-language card: what is being done
 * and whether it finished; never the code, a terminal or a file tree. Code
 * workspaces add the technical details and the live output.
 */
/** a file's size in the interface language's number format: 36,7 KB in Turkish */
export function formatSize(bytes: number): string {
  return formatBytes(bytes, currentLocale());
}

/** Uploads of a message; a readable one shows as a document with its reading state and size (DOC-002, DOC-008). */
export function AttachmentChips({
  conversationId,
  attachments,
  documents,
  onRemove,
}: {
  conversationId: string;
  attachments: Attachment[];
  documents?: DocumentDto[];
  onRemove?: (fileId: string) => void;
}) {
  const { t } = useTranslation();
  if (attachments.length === 0) return null;
  return (
    <ul className="chips" data-testid="attachments">
      {attachments.map((file) => {
        const doc = documents?.find((d) => d.fileId === file.fileId);
        const size = doc ? documentSize(doc, t) : null;
        return (
        <li key={file.fileId} className="chip attachment-chip" data-testid="attachment-chip" data-status={doc?.status ?? 'file'}>
          <KindBadge kind={doc?.kind} name={file.name} />
          <a href={`/api/conversations/${conversationId}/files/${file.fileId}`} download={file.name}>
            {file.name}
          </a>
          <span className="muted"> · {size ?? formatSize(file.size)}</span>
          {doc && doc.status !== 'ready' ? <DocumentState doc={doc} /> : null}
          {onRemove ? (
            <Button size="sm" variant="ghost" aria-label={t('conversation.removeAttachment')} onClick={() => onRemove(file.fileId)}>
              ×
            </Button>
          ) : null}
        </li>
        );
      })}
    </ul>
  );
}

type ToolItem = Extract<ChatItem, { kind: 'tool' }>;

export function ToolCard({ item, profile, output }: { item: ToolItem; profile: 'chat' | 'code'; output: string | undefined }) {
  const { t } = useTranslation();
  const edit = item.tool === 'edit' || item.tool === 'write_file' || item.tool === 'append_file';
  // 16b.1: a change that failed (a call the output limit cut) added no lines
  const stats = profile === 'code' && edit && item.status !== 'failed' ? changeStats(item.tool, item.arguments) : null;
  const Icon = item.status === 'done' ? Icons.done : item.status === 'failed' ? Icons.failed : edit ? Icons.edit : Icons.terminal;
  return (
    <div className={`tool-card tool-card--${item.status}`} data-testid="tool-card" data-status={item.status} data-tool={item.tool}>
      <div className="tool-card__head">
        {item.status === 'running' ? <span className="ui-spinner tool-card__icon" aria-hidden="true" /> : <Icon size={18} className="tool-card__icon" />}
        <span className="ui-visually-hidden">{t(`conversation.tool.${item.status}`)}</span>
        <strong className="tool-card__title">{item.title}</strong>
        {stats ? (
          <span className="tool-card__stats">
            <span className="tool-card__add" aria-hidden="true">+{stats.added}</span>
            <span className="tool-card__del" aria-hidden="true">−{stats.removed}</span>
            <span className="ui-visually-hidden">{t('diff.stats', { added: stats.added, removed: stats.removed })}</span>
          </span>
        ) : null}
        {item.durationMs !== null && item.durationMs >= 100 ? <span className="tool-card__time">{t('conversation.tool.seconds', { seconds: (item.durationMs / 1000).toFixed(1) })}</span> : null}
      </div>
      {item.grantedBy ? (
        <p className="muted tool-card__granted" data-testid="tool-granted-by">
          {t('skillChip.grantedBy', { name: item.grantedBy })}
        </p>
      ) : null}
      {item.allowedBy ? (
        <p className="muted tool-card__granted" data-testid="tool-allowed-by">
          {t(`steps.allowedBy.${item.allowedBy}`)}
        </p>
      ) : null}
      {item.summary && !edit ? <p className="tool-card__summary">{item.summary}</p> : null}
      {profile === 'code' && edit && item.arguments ? (
        <details className="tool-card__details" open={item.tool === 'edit'} data-testid="tool-diff">
          <summary>{t('diff.show')}</summary>
          <DiffView tool={item.tool} args={item.arguments} />
        </details>
      ) : null}
      {profile === 'code' && ((item.details && !edit) || output || item.result) ? (
        <details className="tool-card__details" open={Boolean(output) && item.status === 'running'} data-testid="tool-details">
          <summary>{t('conversation.tool.details')}</summary>
          {item.details && !edit ? <pre className="code-block">{item.details}</pre> : null}
          {item.result && !output ? (
            <div className="tool-card__output">
              <p className="muted">{t('conversation.tool.result')}</p>
              <pre className="code-block" data-testid="tool-result">
                {item.result}
              </pre>
            </div>
          ) : null}
          {output ? (
            <div className="tool-card__output">
              <p className="muted">{t('conversation.tool.output')}</p>
              <pre className="code-block">{output}</pre>
            </div>
          ) : null}
        </details>
      ) : null}
    </div>
  );
}

type ArtifactItem = Extract<ChatItem, { kind: 'artifact' }>;

const KINDS = ['image', 'document', 'spreadsheet', 'presentation', 'pdf', 'csv', 'html', 'markdown', 'text', 'archive', 'other'];

const TILES: Record<string, string> = { document: 'DOCX', spreadsheet: 'XLSX', presentation: 'PPTX', pdf: 'PDF', csv: 'CSV', html: 'HTML', markdown: 'MD', text: 'TXT', archive: 'ZIP' };

/** Something the conversation made (D-35): its kind, size and version, a preview where the browser can show it, a download. */
export function ArtifactCard({ item }: { item: ArtifactItem }) {
  const { t } = useTranslation();
  const href = `/api/artifacts/${item.artifactId}/download`;
  const kind = KINDS.includes(item.artifactKind) ? item.artifactKind : 'other';
  const name = item.name.split('/').pop() ?? item.name;
  const extension = name.includes('.') ? name.split('.').pop()?.toUpperCase().slice(0, 4) : undefined;
  const previewable = kind === 'image' || kind === 'pdf' || kind === 'html' || kind === 'text' || kind === 'markdown' || kind === 'csv';
  return (
    <div className="artifact-card" data-testid="artifact-card" data-name={item.name}>
      {item.artifactKind === 'image' ? (
        <img className="artifact-card__thumb" src={`${href}?inline=1`} alt={name} loading="lazy" />
      ) : (
        <span className={`artifact-card__tile artifact-card__tile--${kind}`} aria-hidden="true">
          {TILES[kind] ?? extension ?? <Icons.file size={18} />}
        </span>
      )}
      <div className="artifact-card__body">
        <span className="artifact-card__name">{name}</span>
        <span className="artifact-card__meta">
          {t(`conversation.artifact.kinds.${kind}`)} · {formatSize(item.size)}
          {item.version > 1 ? ` · ${t('conversation.artifact.version', { version: item.version })}` : ''}
        </span>
      </div>
      <div className="artifact-card__actions">
        {previewable ? (
          <a className="ui-button ui-button--secondary ui-button--sm" href={`${href}?inline=1`} target="_blank" rel="noreferrer noopener" data-testid="artifact-preview">
            {t('conversation.artifact.preview')}
          </a>
        ) : null}
        <a className="ui-button ui-button--secondary ui-button--sm" href={href} download={name} data-testid="artifact-download">
          <Icons.download size={14} />
          {t('conversation.artifact.download')}
        </a>
      </div>
    </div>
  );
}
